import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ComplianceAccessibilityService } from '@client/app/blocks/service/compliance/compliance-accessibility/compliance-accessibility.service';
import { AccessibilityResultTypeService } from '@client/app/blocks/service/accessibility/result-type.service';
import { CrawlHistoryInfoService } from '@client/app/core/services/crawl-history-info/crawl-history-info.service';

@Component({
    selector: 'accessibility-dashboard-compliance',
    standalone: false,
    template: `
        <div class="card panel-monsido-card">
            <div class="card-header">
                <div class="d-flex mt-2">
                    <div>
                        <span class="mr-2 fa-2x" [ngClass]="theme"></span>
                    </div>
                    <div>
                        <h2 class="font-weight-300 mb-1 mt-0">
                            {{header}}
                        </h2>
                        <span class="text-grey">
                            {{'[[passedChecks]] of [[checksInError]] checks passed' | translate:'':{passedChecks: (passedChecks | monNumber), checksInError: (checksCount | monNumber)} }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="card-body">
                <mon-spinner [progress]="progress">
                    <div class="row">
                        <div class="col-lg-20 col-48 pr-none">
                            <a class="text-grey-1"
                               [uiSref]="'base.customer.domain.accessibility.issues'"
                               [uiParams]="{abbr: type}">
                                <accessibility-dashboard-compliance-doughnut [checks]="checks"
                                                                             [type]="type"
                                                                             [selectedObs]="selectedObs">
                                </accessibility-dashboard-compliance-doughnut>
                            </a>
                            <div class="col-48">
                                <div class="text-grey mt-3 ml-1" *ngIf="hasError() && !accessibilityErrorsOnly">
                                    <div class="inline-block outer-r-small">
                                        <span class="fas fa-lg fa-bug outer-r-smurf" aria-hidden="true"></span>
                                        <span class="sr-only" translate>Error</span>
                                        <span>{{error.length}}</span>
                                    </div>
                                    <div class="inline-block mr-2">
                                        <span class="fas fa-lg fa-bell outer-r-smurf" aria-hidden="true"></span>
                                        <span class="sr-only" translate>Warning</span>
                                        <span>{{warning.length}}</span>
                                    </div>
                                    <div class="inline-block mr-2">
                                        <span class="fas fa-lg fa-eye outer-r-smurf" aria-hidden="true"></span>
                                        <span class="sr-only" translate>Review</span>
                                        <span>{{review.length}}</span>
                                    </div>
                                </div>
                                <div class="mt-3 ml-1 no-error-text" *ngIf="!hasError()" translate>
                                    Well done - no accessibility issues found
                                </div>
                            </div>
                        </div>
                        <div class="col-48 col-lg-28">
                            <accessibility-dashboard-compliance-bar [history]="history"
                                                                    [checks]="checks"
                                                                    [selectedObs]="selectedObs"
                                                                    [type]="type">
                            </accessibility-dashboard-compliance-bar>
                        </div>
                    </div>
                </mon-spinner>
            </div>
        </div>
    `,
})
export class AccessibilityDashboardComplianceComponent implements OnInit, OnDestroy {
    @Input() theme: any;
    @Input() header: any;
    @Input() type: any;
    @Input() lastCrawlTxt: any;
    @Input() color: any;
    @Input() history: any;
    @Input() checks: any;
    @Input() progress: any;
    @Input() selectedObs: any;

    accessibilityErrorsOnly = true;
    destroyed$ = new Subject<void>();
    passedChecks = 0;
    checksCount: any;
    review: any;
    error: any;
    warning: any;

    constructor (
        private complianceAccessibilityService: ComplianceAccessibilityService,
        private accessibilityResultTypeService: AccessibilityResultTypeService,
        private crawlHistoryInfoService: CrawlHistoryInfoService,
    ) {}

    ngOnInit () {
        this.passedChecks = 0;
        this.crawlHistoryInfoService.accessibilityErrorsOnly$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((value) => {
                this.accessibilityErrorsOnly = value;
            });
        this.selectedObs.subscribe((resultType: any) => {
            const params = {
                result_types: ['review'],
                only_errors: true,
                abbreviations: [this.type],
            };
            this.review = this.complianceAccessibilityService.filterAccessibilityChecksByResultTypes(this.checks, params);
            params.result_types = ['error'];
            this.error = this.complianceAccessibilityService.filterAccessibilityChecksByResultTypes(this.checks, params);
            params.result_types = ['warning'];
            this.warning = this.complianceAccessibilityService.filterAccessibilityChecksByResultTypes(this.checks, params);
            this.checksCount = this.getChecksCount(resultType);
            this.passedChecks = this.getPassedChecksCount(resultType);
        });
    }

    ngOnDestroy () {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    getPassedChecksCount (resultType: any) {
        const checksCount = this.getChecksCount(resultType);
        const checksWithIssuesCount = this.accessibilityResultTypeService.getChecksCount(
            this.checks,
            resultType,
            [this.type],
            true,
        );
        return checksCount - checksWithIssuesCount;
    }

    getChecksCount (resultType: any) {
        return this.accessibilityResultTypeService.getChecksCount(this.checks, resultType, [this.type]);
    }

    hasError () {
        if (!Array.isArray(this.error) && !Array.isArray(this.review) && !Array.isArray(this.warning)) {
            return false;
        }
        return this.review.length || this.error.length || this.warning.length;
    }
}
